import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Radar, PauseCircle, PlayCircle, Clock, Link2, Copy, RefreshCw, Shield, Database, Send, Mic2 } from "lucide-react";
import { VoiceChip } from "@/lib/voiceColors";

export default function Settings() {
  const { workspaceId } = useParams();
  const [ws, setWs] = useState(null);
  const [status, setStatus] = useState(null);
  const [apify, setApify] = useState(null);
  const [usage, setUsage] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [portal, setPortal] = useState(null);

  const loadWs = () => apiGet(`/workspaces/${workspaceId}`).then(setWs);
  const loadApify = () => apiGet(`/workspaces/${workspaceId}/apify/settings`).then(setApify);
  const loadUsage = () => apiGet(`/settings/apify/usage`).then(setUsage).catch(() => setUsage(null));
  const loadPortal = () => apiGet(`/workspaces/${workspaceId}/portal-token`).then(setPortal).catch(() => setPortal(null));

  useEffect(() => {
    loadWs();
    loadApify();
    loadUsage();
    loadPortal();
    apiGet(`/settings/integrations`).then(setStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const triggerScrape = async () => {
    setScraping(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/scrape`, {});
      const runId = r.run_id;
      toast.success("scrape running in background", {
        description: "1-3 min depending on contact count.",
      });
      const startedAt = Date.now();
      const maxMs = 6 * 60 * 1000;
      while (Date.now() - startedAt < maxMs) {
        await new Promise((res) => setTimeout(res, 5000));
        try {
          const s = await apiGet(
            `/workspaces/${workspaceId}/scrape/status${runId ? `?run_id=${runId}` : ""}`
          );
          if (s?.status === "succeeded") {
            const res = s.result || {};
            toast.success(`${res.new_posts || 0} new posts · ${res.drafts_created || 0} drafts`);
            loadWs();
            break;
          }
          if (s?.status === "failed") {
            toast.error(s.error || "scrape failed");
            break;
          }
        } catch {
          // transient — keep polling
          continue;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "scrape failed to start");
    } finally {
      setScraping(false);
    }
  };

  const toggleSetting = async (key, value) => {
    try {
      await apiPatch(`/workspaces/${workspaceId}/apify/settings`, { [key]: value });
      toast.success("Saved");
      loadApify();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Layout title="Settings" subtitle={ws?.name || ""}>
      <div className="max-w-3xl space-y-5">
        <Section title="Integrations">
          <Item
            ok={status?.llm_configured}
            label="Claude Sonnet 4.5 (Emergent Universal LLM Key)"
            okText="Connected — used for comment generation"
            warnText="Not configured. Top up Universal Key in Emergent Profile."
          />
          <Item
            ok={status?.apify_configured}
            label="Apify (LinkedIn post scraping)"
            okText={`Connected · actor: ${status?.apify_actor || "harvestapi/linkedin-profile-posts"}`}
            warnText="Not configured yet. Until then, paste posts manually via Feed → Add post."
          />
        </Section>

        {status?.apify_configured && apify && (
          <Section title="Apify spend control">
            <div className="surface p-4 space-y-4" data-testid="apify-spend-panel">
              {usage && (
                <div className="border-b border-zinc-800 pb-4" data-testid="apify-usage-tracker">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="text-sm text-zinc-100">
                      ${usage.used_usd.toFixed(2)}{" "}
                      <span className="text-zinc-500">of ${usage.limit_usd.toFixed(0)} used</span>
                    </div>
                    <div className="text-xs text-zinc-500">
                      {usage.days_left_in_cycle ?? "?"} days left · plan: {usage.plan_id?.toLowerCase()}
                    </div>
                  </div>
                  <div className="w-full bg-zinc-900 border border-zinc-800 rounded-sm h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        usage.pct >= 80 ? "bg-rose-500" : usage.pct >= 50 ? "bg-amber-400" : "bg-emerald-400"
                      }`}
                      style={{ width: `${Math.min(usage.pct, 100)}%` }}
                      data-testid="apify-usage-bar"
                    />
                  </div>
                  {usage.pct >= 80 && (
                    <div className="text-xs text-rose-400 mt-2">
                      ⚠ over 80%. consider pausing auto-scrape until next cycle.
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 flex items-center gap-2">
                    {apify.auto_scrape_paused ? (
                      <PauseCircle className="w-4 h-4 text-amber-400" />
                    ) : (
                      <PlayCircle className="w-4 h-4 text-emerald-400" />
                    )}
                    Auto-scrape {apify.auto_scrape_paused ? "paused" : "on"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    <Clock className="w-3 h-3 inline mr-1" /> {apify.schedule}. Pulls comments on your contacts' posts (for ENGAGE drafts).
                  </div>
                </div>
                <Switch
                  data-testid="auto-scrape-toggle"
                  checked={!apify.auto_scrape_paused}
                  onCheckedChange={(v) => toggleSetting("auto_scrape_paused", !v)}
                />
              </div>

              <div className="border-t border-zinc-800 pt-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100">
                    Auto-pull your own posts + reply comments
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Disabled by default — keeps your Apify bill low. RESPOND tab still works via manual "Scan my posts" or "Find post" buttons.
                  </div>
                </div>
                <Switch
                  data-testid="auto-respond-toggle"
                  checked={!!apify.auto_respond_scrape}
                  onCheckedChange={(v) => toggleSetting("auto_respond_scrape", v)}
                />
              </div>

              <div className="border-t border-zinc-800 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-zinc-100">Last scheduled runs</div>
                  <Button
                    onClick={triggerScrape}
                    disabled={scraping}
                    className="btn-ghost"
                    data-testid="settings-scrape-btn"
                  >
                    <Radar className={`w-3.5 h-3.5 mr-1.5 ${scraping ? "animate-pulse" : ""}`} />
                    {scraping ? "Scanning…" : "Run now"}
                  </Button>
                </div>
                {apify.recent_runs?.length ? (
                  <ul className="space-y-1.5">
                    {apify.recent_runs.slice(0, 5).map((r) => (
                      <li key={r.id} className="text-xs text-zinc-500 flex items-center gap-3 flex-wrap" data-testid={`run-${r.id}`}>
                        <span className="text-zinc-400 mono">
                          {new Date(r.started_at).toLocaleString()}
                        </span>
                        {r.error ? (
                          <span className="text-rose-400">error</span>
                        ) : (
                          <>
                            {r.result?.engage && (
                              <span>
                                engage · {r.result.engage.new_posts || 0} new posts, {r.result.engage.drafts_created || 0} drafts
                              </span>
                            )}
                            {r.result?.respond && (
                              <span>
                                · respond · {r.result.respond.comments_added || 0} comments, {r.result.respond.replies_added || 0} replies
                              </span>
                            )}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-zinc-500">No scheduled runs yet — last manual scrape: {apify.last_scrape_at ? new Date(apify.last_scrape_at).toLocaleString() : "never"}</div>
                )}
              </div>
            </div>
          </Section>
        )}

        <Section title="Client portal">
          <div className="surface p-4 space-y-4" data-testid="portal-settings-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-sky-400" /> Public share link
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  read-only Kanban for your client. they can recommend changes, submit their own drafts, and approve posts — no login needed.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="portal-create-btn"
                  className="btn-ghost"
                  onClick={async () => {
                    try {
                      const r = portal?.token
                        ? portal
                        : await apiPost(`/workspaces/${workspaceId}/portal-token`, {});
                      const url = `${window.location.origin}${r.url_path}`;
                      await navigator.clipboard.writeText(url);
                      toast.success("link copied", { description: url, duration: 6000 });
                      loadPortal();
                    } catch {
                      toast.error("could not create link");
                    }
                  }}
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  {portal?.token ? "Copy link" : "Create link"}
                </Button>
                {portal?.token && (
                  <Button
                    data-testid="portal-rotate-btn"
                    className="btn-ghost"
                    onClick={async () => {
                      if (!window.confirm("rotate the link? the old one will stop working.")) return;
                      try {
                        await apiPost(`/workspaces/${workspaceId}/portal-token`, {});
                        toast.success("new link generated");
                        loadPortal();
                      } catch {
                        toast.error("rotate failed");
                      }
                    }}
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> New link
                  </Button>
                )}
              </div>
            </div>
            {portal?.url_path && (
              <div className="mono text-xs text-zinc-400 break-all border-t border-zinc-800 pt-3">
                {window.location.origin}{portal.url_path}
              </div>
            )}

            <div className="border-t border-zinc-800 pt-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-400" /> Allow client to approve posts
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  shows an "approve this post" button in the drawer. turn off if your client doesn't run a sign-off step (e.g. LeanSuite).
                </div>
              </div>
              <Switch
                data-testid="portal-approval-toggle"
                checked={portal?.approval_enabled !== false}
                onCheckedChange={async (v) => {
                  try {
                    await apiPatch(`/workspaces/${workspaceId}/portal-settings`, { approval_enabled: v });
                    toast.success("saved");
                    loadPortal();
                  } catch {
                    toast.error("save failed");
                  }
                }}
              />
            </div>
          </div>
        </Section>

        <ClientAccountsSection workspaceId={workspaceId} />

        <VoiceProfilesSection workspaceId={workspaceId} />

        <NotionSection workspaceId={workspaceId} />

        <Section title="Workspace identifiers">
          <div className="text-xs text-stone mb-3">
            Note: the first account created on this app becomes the admin. Everyone else who signs up joins as a client.
          </div>
          <div className="surface p-4 space-y-2 mono text-xs">
            <div>
              <span className="overline mr-2" style={{ fontSize: 9 }}>
                workspace id
              </span>
              <span className="text-zinc-300">{workspaceId}</span>
            </div>
            {ws?.linkedin_url && (
              <div>
                <span className="overline mr-2" style={{ fontSize: 9 }}>
                  linkedin
                </span>
                <span className="text-zinc-300">{ws.linkedin_url}</span>
              </div>
            )}
          </div>
        </Section>
      </div>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="overline mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Item({ ok, label, okText, warnText }) {
  return (
    <div className="surface p-4 flex items-start gap-3">
      <div className="mt-0.5">
        {ok ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        ) : (
          <AlertCircle className="w-4 h-4 text-amber-400" />
        )}
      </div>
      <div>
        <div className="text-sm text-zinc-100">{label}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{ok ? okText : warnText}</div>
      </div>
    </div>
  );
}


// ============================================================
// NOTION SYNC — one-way push to client's Notion database
// ============================================================
function NotionSection({ workspaceId }) {
  const [cfg, setCfg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [token, setToken] = React.useState("");
  const [dbId, setDbId] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [test, setTest] = React.useState(null);

  const load = () =>
    apiGet(`/workspaces/${workspaceId}/notion/config`)
      .then((d) => { setCfg(d); setLoading(false); })
      .catch(() => { setCfg(null); setLoading(false); });

  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspaceId]);

  if (loading) return null;

  const configured = !!cfg?.configured;

  const save = async () => {
    if (!token.trim() || !dbId.trim()) {
      toast.error("token + database id required");
      return;
    }
    setSaving(true);
    try {
      await apiPut(`/workspaces/${workspaceId}/notion/config`, {
        token: token.trim(),
        database_id: dbId.trim().replace(/[^a-fA-F0-9-]/g, ""),
      });
      toast.success("notion saved · running test connection");
      setToken("");
      setDbId("");
      await load();
      await runTest();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "save failed");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/notion/test`, {});
      setTest(r);
      toast.success(`connected to "${r.database_title || "notion"}"`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "test failed");
    } finally {
      setTesting(false);
    }
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/notion/sync-all`, {});
      if (r.failed_count > 0) {
        toast.warning(`${r.synced} synced · ${r.failed_count} failed`, {
          description: r.failures?.[0]?.error,
        });
      } else {
        toast.success(`${r.synced} posts pushed to notion`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("disconnect notion? existing notion pages stay; we just forget the link.")) return;
    try {
      await apiDelete(`/workspaces/${workspaceId}/notion/config`);
      toast.success("disconnected");
      setTest(null);
      await load();
    } catch (e) {
      toast.error("disconnect failed");
    }
  };

  return (
    <Section title="Notion sync (one-way)">
      <div className="surface p-4 space-y-4" data-testid="notion-settings-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-sm text-zinc-100 flex items-center gap-2">
              <Database className="w-4 h-4 text-sky-400" /> Mirror every post into your client's Notion DB
            </div>
            <div className="text-xs text-zinc-500 mt-0.5 leading-relaxed max-w-2xl">
              create an internal integration at <span className="mono">notion.so/my-integrations</span>, share your target database with it, then paste the secret + database id below. captions are pushed verbatim — newlines and unicode bold glyphs preserved.
            </div>
          </div>
        </div>

        {!configured && (
          <div className="space-y-2 pt-2 border-t border-zinc-800">
            <div>
              <Label className="text-xs text-zinc-400">notion integration secret</Label>
              <Input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="secret_…"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mono"
                data-testid="notion-token-input"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">database id (or paste full URL)</Label>
              <Input
                value={dbId}
                onChange={(e) => setDbId(e.target.value)}
                placeholder="32-char id from the database URL"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mono"
                data-testid="notion-db-input"
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={save}
                disabled={saving || !token.trim() || !dbId.trim()}
                className="btn-primary"
                data-testid="notion-save-btn"
              >
                {saving ? "saving…" : "connect notion"}
              </Button>
            </div>
          </div>
        )}

        {configured && (
          <div className="pt-2 border-t border-zinc-800 space-y-3">
            <div className="grid grid-cols-2 gap-3 mono text-xs">
              <div>
                <div className="overline" style={{ fontSize: 9 }}>token</div>
                <div className="text-zinc-300">{cfg.token_preview}</div>
              </div>
              <div>
                <div className="overline" style={{ fontSize: 9 }}>database</div>
                <div className="text-zinc-300 truncate">{cfg.database_title || cfg.database_id}</div>
              </div>
              <div>
                <div className="overline" style={{ fontSize: 9 }}>last sync</div>
                <div className="text-zinc-300">{cfg.last_synced_at ? new Date(cfg.last_synced_at).toLocaleString() : "—"}</div>
              </div>
              <div>
                <div className="overline" style={{ fontSize: 9 }}>last error</div>
                <div className={cfg.last_sync_error ? "text-rose-400" : "text-zinc-300"}>
                  {cfg.last_sync_error || "none"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={runTest}
                disabled={testing}
                className="btn-ghost"
                data-testid="notion-test-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${testing ? "animate-spin" : ""}`} />
                {testing ? "testing…" : "test connection"}
              </Button>
              <Button
                onClick={syncAll}
                disabled={syncing}
                className="btn-primary"
                data-testid="notion-sync-all-btn"
              >
                <Send className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-pulse" : ""}`} />
                {syncing ? "syncing…" : "sync all posts now"}
              </Button>
              <Button
                onClick={disconnect}
                className="btn-ghost text-rose-400 hover:text-rose-300"
                data-testid="notion-disconnect-btn"
              >
                disconnect
              </Button>
            </div>
            {test?.properties?.length > 0 && (
              <div className="border-t border-zinc-800 pt-3">
                <div className="overline mb-2" style={{ fontSize: 9 }}>detected schema</div>
                <div className="flex flex-wrap gap-1.5">
                  {test.properties.map((p) => (
                    <span
                      key={p.name}
                      className="mono text-[10px] text-zinc-400 bg-zinc-900 border border-zinc-800 px-2 py-0.5"
                    >
                      {p.name} <span className="text-zinc-600">· {p.type}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Section>
  );
}

// ============================================================
// VOICE PROFILES — manage each voice's LinkedIn profile URL.
// "Scan my posts" needs this URL; without it the scan returns nothing.
// ============================================================
function ClientAccountsSection({ workspaceId }) {
  const [members, setMembers] = React.useState([]);
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [issued, setIssued] = React.useState(null); // { email, password, portal_url_path }

  const load = React.useCallback(async () => {
    try {
      const rows = await apiGet(`/workspaces/${workspaceId}/client-members`);
      setMembers(Array.isArray(rows) ? rows : []);
    } catch { setMembers([]); }
  }, [workspaceId]);

  React.useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim()) return toast.error("email is required");
    setLoading(true);
    try {
      const res = await apiPost(`/workspaces/${workspaceId}/invite-client`, {
        email: email.trim(),
        name: name.trim() || undefined,
      });
      setIssued(res);
      setEmail(""); setName("");
      load();
      toast.success("client account created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "invite failed");
    } finally { setLoading(false); }
  };

  const copyCredentials = async () => {
    if (!issued) return;
    const portalUrl = `${window.location.origin}${issued.portal_url_path}`;
    const loginUrl = `${window.location.origin}${issued.login_url_path}`;
    const text = `Login: ${loginUrl}\nEmail: ${issued.email}\nPassword: ${issued.password}\nPortal (after login): ${portalUrl}`;
    try { await navigator.clipboard.writeText(text); toast.success("copied to clipboard"); }
    catch { toast.error("copy failed"); }
  };

  return (
    <Section title="Client accounts">
      <div className="surface p-4 space-y-4" data-testid="client-accounts-panel">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="overline text-[10px] text-stone mb-1 block">Email</label>
            <Input
              type="email"
              placeholder="client@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="invite-client-email"
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="overline text-[10px] text-stone mb-1 block">Name (optional)</label>
            <Input
              placeholder="Angad S."
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="invite-client-name"
            />
          </div>
          <Button
            size="sm"
            onClick={invite}
            disabled={loading}
            data-testid="invite-client-btn"
          >
            {loading ? "Creating…" : "Invite client"}
          </Button>
        </div>

        {issued && (
          <div className="rounded-md border border-emerald-800 bg-emerald-500/5 p-4 space-y-2" data-testid="invite-credentials">
            <div className="text-sm font-semibold text-emerald-300">Account created — copy and share now.</div>
            <div className="mono text-xs text-parchment space-y-0.5">
              <div><span className="text-stone">login:</span> <span className="select-all">{`${window.location.origin}/`}</span></div>
              <div><span className="text-stone">email:</span> <span className="select-all">{issued.email}</span></div>
              <div><span className="text-stone">password:</span> <span className="select-all">{issued.password}</span></div>
              <div><span className="text-stone">portal:</span> <span className="select-all">{`${window.location.origin}${issued.portal_url_path}`}</span></div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={copyCredentials} data-testid="invite-copy-credentials">
                Copy all
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIssued(null)}>Dismiss</Button>
            </div>
            <div className="text-[11px] text-stone">
              This password is shown once. Rotate it by re-inviting the same email.
            </div>
          </div>
        )}

        {members.length > 0 && (
          <div className="pt-2 border-t border-hairline" data-testid="client-members-list">
            <div className="overline text-[11px] text-stone mb-2">Existing client accounts</div>
            <div className="space-y-1">
              {members.map((m) => (
                <div key={m.user_id} className="text-xs flex items-center justify-between py-1">
                  <span className="text-parchment">{m.name} <span className="text-stone">· {m.email}</span></span>
                  <span className="mono text-[10px] text-stone">joined {(m.invited_at || m.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}


function VoiceProfilesSection({ workspaceId }) {
  const [voices, setVoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [edits, setEdits] = React.useState({}); // id → new url
  const [saving, setSaving] = React.useState({});

  const load = async () => {
    try {
      const vps = await apiGet(`/workspaces/${workspaceId}/voice_profiles`);
      setVoices(vps || []);
    } catch {
      setVoices([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspaceId]);

  if (loading) return null;
  if (voices.length === 0) return null;

  const save = async (vp, value) => {
    const url = (value || "").trim();
    setSaving((s) => ({ ...s, [vp.id]: true }));
    try {
      await apiPatch(`/workspaces/${workspaceId}/voice_profiles/${vp.id}`, {
        linkedin_url: url,
      });
      toast.success(`saved · ${vp.name}`);
      setEdits((e) => { const c = { ...e }; delete c[vp.id]; return c; });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "save failed");
    } finally {
      setSaving((s) => ({ ...s, [vp.id]: false }));
    }
  };

  const missingCount = voices.filter((v) => !(v.linkedin_url || "").trim()).length;

  return (
    <Section title="Voice profiles">
      <div className="surface p-4 space-y-3" data-testid="voice-profiles-panel">
        <div className="flex items-start gap-2">
          <Mic2 className="w-4 h-4 text-sky-400 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-zinc-100">paste each voice's LinkedIn profile URL</div>
            <div className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
              the <span className="mono">scan posts</span> button on the respond tab uses this URL to fetch posts from. without it, scans return nothing.
            </div>
            {missingCount > 0 && (
              <div className="text-xs text-amber-300 mt-2 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                {missingCount} of {voices.length} voice{voices.length === 1 ? "" : "s"} {missingCount === 1 ? "is" : "are"} missing a URL.
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3 pt-2 border-t border-zinc-800">
          {voices.map((vp) => {
            const current = edits[vp.id] !== undefined ? edits[vp.id] : (vp.linkedin_url || "");
            const dirty = current.trim() !== (vp.linkedin_url || "").trim();
            return (
              <div key={vp.id} className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 items-center"
                data-testid={`voice-row-${vp.id}`}
              >
                <VoiceChip
                  voiceId={vp.id}
                  voiceName={vp.name}
                  variant="dark"
                  size="md"
                />
                <Input
                  value={current}
                  placeholder="https://www.linkedin.com/in/your-handle/"
                  onChange={(e) => setEdits((s) => ({ ...s, [vp.id]: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 mono text-xs"
                  data-testid={`voice-linkedin-input-${vp.id}`}
                />
                <Button
                  onClick={() => save(vp, current)}
                  disabled={!dirty || saving[vp.id]}
                  className={dirty ? "btn-primary" : "btn-ghost"}
                  data-testid={`voice-save-${vp.id}`}
                >
                  {saving[vp.id] ? "saving…" : dirty ? "save" : "saved"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

